import * as Sentry from "@sentry/tanstackstart-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  markAllNotificationsAsRead,
  markNotificationAsRead,
  type Api,
} from "api-client";
import { Icon } from "icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger, Button, Badge } from "ui";
import { cn } from "ui";

import { useAuthStore } from "src/auth";
import { NotificationItem } from "src/components/NotificationItem";
import {
  getNotificationsQueryKey,
  useNotificationsQuery,
} from "src/queries/notifications";

interface NotificationRouteContext {
  isCoach: boolean;
  isAthlete: boolean;
}

function getCalendarItemCommentNavigateParams(
  notification: Api.GetAllNotifications.Output.Notification,
  comment: Api.GetAllNotifications.Output.Comment,
  ctx: NotificationRouteContext,
) {
  if (!comment.exerciseId) {
    Sentry.captureException(
      new Error("Missing exerciseId for calendar item comment notification"),
      { extra: { commentId: comment.id } },
    );
    return null;
  }

  if (comment.workflowId && comment.programWorkoutId) {
    if (ctx.isCoach) {
      return {
        to: `/workflows/$workflowId`,
        params: {
          workflowId: comment.workflowId,
        },
        search: {
          date: comment.programWorkoutDate ?? undefined,
          selectedWorkout: comment.programWorkoutId,
          workflowComments: comment.baseWorkoutItemId ?? comment.workoutItemId,
          workflowCommentsAuthor: comment.athleteUserId ?? undefined,
        },
      } as const;
    }

    if (ctx.isAthlete) {
      return {
        to: `/`,
        search: {
          "workflow-workout": comment.programWorkoutId,
          workflow: comment.workflowId,
          comments: comment.exerciseId,
        },
      } as const;
    }
  }

  if (ctx.isAthlete && comment.calendarItemId) {
    return {
      to: `/`,
      search: {
        comments: comment.exerciseId,
        "calendar-item": comment.calendarItemId,
      },
    } as const;
  }

  if (ctx.isCoach && comment.athleteId && comment.calendarItemId) {
    // A calendar comment belongs on the calendar - open the item modal with the
    // comment thread on the coach's view of the athlete's calendar, mirroring
    // the athlete branch above (works whether or not the workout has a program).
    return {
      to: "/athletes/$athleteId/calendar",
      params: {
        athleteId: comment.athleteId,
      },
      search: {
        comments: comment.exerciseId,
        "calendar-item": comment.calendarItemId,
      },
    } as const;
  }

  return null;
}

function getWorkflowCommentNavigateParams(
  notification: Api.GetAllNotifications.Output.Notification,
  comment: Api.GetAllNotifications.Output.Comment,
  ctx: NotificationRouteContext,
) {
  if (!comment.workflowId || !comment.programWorkoutId) {
    return null;
  }

  if (!comment.exerciseId) {
    Sentry.captureException(
      new Error("Missing exerciseId for workflow comment notification"),
      { extra: { commentId: comment.id } },
    );
    return null;
  }

  if (ctx.isCoach) {
    return {
      to: `/workflows/$workflowId`,
      params: {
        workflowId: comment.workflowId,
      },
      search: {
        date: comment.programWorkoutDate ?? undefined,
        selectedWorkout: comment.programWorkoutId,
        workflowComments: comment.baseWorkoutItemId ?? comment.workoutItemId,
        workflowCommentsAuthor: comment.athleteUserId ?? undefined,
      },
    } as const;
  }

  if (ctx.isAthlete) {
    return {
      to: `/`,
      search: {
        "workflow-workout": comment.programWorkoutId,
        workflow: comment.workflowId,
        comments: comment.exerciseId,
      },
    } as const;
  }

  return null;
}

function getProgramCommentNavigateParams(
  notification: Api.GetAllNotifications.Output.Notification,
  comment: Api.GetAllNotifications.Output.Comment,
  ctx: NotificationRouteContext,
) {
  if (!comment.exerciseId) {
    Sentry.captureException(
      new Error("Missing exerciseId for program comment notification"),
      { extra: { commentId: comment.id } },
    );
    return null;
  }

  if (ctx.isCoach && comment.athleteId && comment.programId) {
    return {
      to: "/athletes/$athleteId/programs/$programId",
      params: {
        athleteId: comment.athleteId,
        programId: comment.programId,
      },
      search: {
        comments: comment.exerciseId,
        selectedWorkout: comment.programWorkoutId ?? undefined,
        workoutItemId: comment.baseWorkoutItemId ?? comment.workoutItemId,
      },
    } as const;
  }

  if (ctx.isAthlete && comment.programId) {
    return {
      to: "/programs/$programId",
      params: {
        programId: comment.programId,
      },
      search: {
        comments: comment.exerciseId,
        selectedWorkout: comment.programWorkoutId ?? undefined,
        workoutItemId: comment.baseWorkoutItemId ?? comment.workoutItemId,
      },
    } as const;
  }

  return null;
}

function getCommentNavigateParams(
  notification: Api.GetAllNotifications.Output.Notification,
  comment: Api.GetAllNotifications.Output.Comment,
  ctx: NotificationRouteContext,
) {
  switch (comment.context) {
    case "CALENDAR_ITEM":
      return getCalendarItemCommentNavigateParams(notification, comment, ctx);
    case "WORKFLOW":
      return getWorkflowCommentNavigateParams(notification, comment, ctx);
    case "PROGRAM":
      return getProgramCommentNavigateParams(notification, comment, ctx);
  }
}

function getJoinRequestNavigateParams(
  notification: Api.GetAllNotifications.Output.Notification,
) {
  if (!notification.workflow) {
    return null;
  }

  return {
    to: `/workflows/$workflowId`,
    params: {
      workflowId: notification.workflow.id,
    },
  } as const;
}

function getNotificationNavigateParams(
  notification: Api.GetAllNotifications.Output.Notification,
  ctx: NotificationRouteContext,
) {
  switch (notification.type) {
    case "ORGANIZATION_MEMBER_INVITED": {
      if (!notification.organization || !notification.organizationInvitation) {
        return null;
      }

      if (notification.organizationInvitation.status === "ACCEPTED") {
        return {
          to: "/orgs/$slug",
          params: { slug: notification.organization.slug },
        } as const;
      }

      return {
        to: "/organization-invitations/$invitationId",
        params: { invitationId: notification.organizationInvitation.id },
      } as const;
    }
    case "COMMENT_ADDED": {
      if (!notification.comment) {
        return null;
      }

      return getCommentNavigateParams(notification, notification.comment, ctx);
    }
    case "JOIN_REQUEST_WORKFLOW":
      return getJoinRequestNavigateParams(notification);
    case "PROFILE_CONTACT_SUBMITTED": {
      if (!notification.profileLead) {
        return null;
      }

      return {
        to: "/profile/leads",
        search: {
          leadId: notification.profileLead.id,
        },
      } as const;
    }
    case "PRODUCT_PURCHASED":
    case "PRODUCT_PAYMENT_FAILED":
    case "PRODUCT_SUBSCRIPTION_CANCEL_SCHEDULED":
    case "PRODUCT_SUBSCRIPTION_ENDED":
    case "PRODUCT_PAYMENT_DISPUTED":
    case "PRODUCT_PAYMENT_RECEIVED":
    case "PRODUCT_SUBSCRIPTION_CANCEL_UNDONE":
      return {
        to: "/athletes/$athleteId/payments",
        params: { athleteId: notification.athlete.id },
      } as const;
    case "ACCEPTED_JOIN_REQUEST_WORKFLOW":
    case "REJECTED_JOIN_REQUEST_WORKFLOW":
    case "PRODUCT_CHECKOUT_ABANDONED":
    case "PRODUCT_ATHLETE_REGISTERED":
      return null;
  }
}

export type NotificationLinkOptions = NonNullable<
  ReturnType<typeof getNotificationNavigateParams>
>;

interface NotificationButtonProps {
  sidebar?: boolean;
}

export function NotificationButton(props: NotificationButtonProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const authStore = useAuthStore();

  const notificationsQuery = useNotificationsQuery();

  const [isOpen, setIsOpen] = useState(false);

  const notifications = notificationsQuery.notifications;
  const unreadCount = notificationsQuery.unreadCount;

  function handleScroll(event: React.UIEvent<HTMLDivElement>) {
    const element = event.currentTarget;
    const reachedBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight < 48;

    if (
      reachedBottom &&
      notificationsQuery.hasNextPage &&
      !notificationsQuery.isFetchingNextPage
    ) {
      notificationsQuery.fetchNextPage();
    }
  }

  const markAllAsReadMutation = useMutation({
    mutationFn: () => {
      return markAllNotificationsAsRead();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getNotificationsQueryKey() });
    },
  });

  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => {
      return markNotificationAsRead({ body: { notificationId: id } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getNotificationsQueryKey() });
    },
  });

  function handleNotificationClick(
    notification: Api.GetAllNotifications.Output.Notification,
    event: React.MouseEvent,
  ) {
    markAsReadMutation.mutate(notification.id);

    const opensNewTab = event.metaKey || event.ctrlKey || event.button === 1;

    if (!opensNewTab) {
      setIsOpen(false);
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={props.sidebar ? "ghost" : "outline"}
          size="icon-sm"
          className={cn(
            "relative flex gap-2",
            props.sidebar
              ? "h-8 w-full justify-start rounded-md px-2 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2!"
              : "size-10 gap-0 rounded-full p-0",
          )}
        >
          <Icon name="tabler-bell" />
          {props.sidebar ? (
            <span className="group-data-[collapsible=icon]:hidden">
              {t("notifications")}
            </span>
          ) : null}
          {unreadCount > 0 && (
            <Badge
              className={cn(
                "absolute flex size-5 items-center justify-center bg-red-500 px-1 text-xs text-white",
                props.sidebar
                  ? "right-1 group-data-[collapsible=icon]:-top-1 group-data-[collapsible=icon]:-right-1"
                  : "-top-1 -right-1",
              )}
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        side={props.sidebar ? "right" : "bottom"}
        align="end"
      >
        <div className="border-0 shadow-none">
          <div className="pb-2 pt-4 px-4 flex flex-col items-center">
            <div className="text-lg font-medium">{t("notifications")}</div>
            {unreadCount > 0 && (
              <div>
                <Button
                  variant="ghost"
                  className="text-xs h-auto p-1 px-3"
                  onClick={() => {
                    markAllAsReadMutation.mutate();
                  }}
                >
                  {t("markAllAsRead")}
                </Button>
              </div>
            )}
          </div>
          <div className="p-0">
            {notifications.length === 0 ? (
              <div className="py-6 px-4 text-center text-muted-foreground">
                {t("noNotifications")}
              </div>
            ) : (
              <div className="max-h-80 overflow-auto" onScroll={handleScroll}>
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    linkOptions={getNotificationNavigateParams(notification, {
                      isCoach: authStore.isCoach,
                      isAthlete: authStore.isAthlete,
                    })}
                    onClick={(event) => {
                      handleNotificationClick(notification, event);
                    }}
                  />
                ))}
                {notificationsQuery.isFetchingNextPage && (
                  <div className="flex justify-center py-3">
                    <Icon
                      name="tabler-loader-2"
                      className="h-4 w-4 animate-spin opacity-50"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          {/* <div className="p-2 bg-slate-50 border-t rounded-b-md">
            <Button
              variant="ghost"
              className="w-full text-sm h-auto py-1.5"
              onClick={() => setIsOpen(false)}
            >
              {t("viewAllNotifications")}
            </Button>
          </div> */}
        </div>
      </PopoverContent>
    </Popover>
  );
}
